/**
 * Route definition for /collection/$slug
 *
 * Uses TanStack Router's loader to prefetch collection data server-side.
 * Fetches collection information and the first page of NFTs before render.
 *
 * @module routes/collection/$slug
 * @category Routes
 */

import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'react-toastify'
import { getCollectionInfo, searchNFTs, ApiError } from '@/lib/api'
import CollectionContainer from '@/containers/Collection'
import { createCollectionSchema, createItemListSchema, createBreadcrumbListSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'

/**
 * Loader function that runs before the component renders.
 *
 * Server-side: fetches collection data and returns it
 * Client-side: provides cached data if available, refetches if stale
 *
 * @param {Object} params - Route parameters
 * @param {string} params.slug - The collection slug/identifier
 * @returns {Promise<{initialCollectionInfo: any, initialNfts: any[], slug: string}>}
 */
export const Route = createFileRoute('/$locale/collection/$slug')({
  /**
   * Preloads the collection data before component mount.
   * Supports SSR - data is fetched on server during initial render
   * and hydrated on client for seamless transition.
   */
  loader: async ({ params: { slug } }) => {
    // Validate slug exists
    if (!slug) {
      throw new Error('Collection slug is required')
    }

    try {
      // Fetch collection info using typed API
      const collectionInfo = await getCollectionInfo({ canonicalName: slug })

      // Resolve the numeric collection id — searchNFTs must receive it,
      // NEVER the slug string.
      const collId = collectionInfo?.id
      if (collId === undefined || collId === null) {
        throw new Error(`Collection resolved but numeric id is missing for "${slug}"`)
      }

      // Fetch first page of NFTs for initial render
      const initialResponse = await searchNFTs(collId, {
        offset: 0,
        limit: 40,
        order: 'listings',
      })
      const initialNfts = initialResponse.items

      // Return data to be available via Route.useLoaderData()
      return {
        initialCollectionInfo: collectionInfo,
        initialNfts,
        slug,
        collectionId: collId,
      }
    } catch (error) {
      // Log error, show toast, and propagate to error boundary
      console.error('Failed to load collection:', error)
      toast.error('Failed to load collection. Please try again.')
      throw error
    }
  },

  /**
   * The component that renders the collection page.
   * Receives loader data via Route.useLoaderData() hook.
   */
  component: CollectionPage,
})

/**
 * Collection page component.
 *
 * Receives preloaded collection data from the loader and passes it to
 * the CollectionContainer. This eliminates the need for useEffect-based
 * data fetching and provides instant rendering.
 *
 * @returns {React.ReactElement}
 */
function CollectionPage() {
  // Get the preloaded data from the route loader
  const { initialCollectionInfo, initialNfts, slug, collectionId } = Route.useLoaderData() as {
    initialCollectionInfo: any
    initialNfts: any[]
    slug: string
    collectionId: string | number
  }

  // Build full URL for the collection
  const pageUrl = `https://stealthisnft.xyz/collection/${slug}`

  // Set dynamic metadata based on actual collection data
  const collectionName = initialCollectionInfo?.title || initialCollectionInfo?.name || slug
  const collectionDescription = initialCollectionInfo?.description || `Browse NFTs from collection ${collectionName}`
  const collectionImage = initialCollectionInfo?.image || initialCollectionInfo?.bannerImage || ''

  // Generate collection schema (CollectionPage)
  const collectionSchema = createCollectionSchema({
    name: collectionName,
    description: collectionDescription,
    url: pageUrl,
    image: collectionImage,
    creator: initialCollectionInfo?.creator,
    count: initialCollectionInfo?.nft_count || initialNfts?.length,
    chain: initialCollectionInfo?.chain,
    contractAddress: initialCollectionInfo?.contract_address,
  })

  // Also generate an ItemList for the first page of NFTs
  const itemListSchema = initialNfts && initialNfts.length > 0
    ? createItemListSchema({
        title: `${collectionName} NFTs`,
        description: `Browse NFTs from ${collectionName}`,
        url: pageUrl,
        items: initialNfts.slice(0, 20).map(nft => ({
          name: nft.title || `NFT #${nft.tokenId}`,
          description: nft.description || '',
          image: nft.img_ipfs || nft.image || '',
          url: nft.route_url || `https://stealthisnft.xyz/item/${nft.chain}/${nft.contract_address}/${nft.tokenId}`,
          price: nft.price,
          currency: 'SGB',
        }))
      })
    : undefined

  // Combine both schemas if both exist
  const breadcrumbSchema = createBreadcrumbListSchema({
    url: pageUrl,
    segments: [
      { name: 'Home', url: 'https://stealthisnft.xyz' },
      { name: 'Collections', url: 'https://stealthisnft.xyz/collections' },
      { name: collectionName },
    ],
  })

  const structuredData = itemListSchema
    ? { '@graph': [breadcrumbSchema, collectionSchema, itemListSchema] }
    : { '@graph': [breadcrumbSchema, collectionSchema] }

  setPageMetadata({
    title: `${collectionName} | VIRI`,
    description: collectionDescription,
    ogImage: collectionImage || undefined,
    ogType: 'website',
    twitterCard: 'summary_large_image',
    jsonLd: structuredData || null,
  })

  return (
    <CollectionContainer
      collectionId={String(collectionId)}
      initialCollectionInfo={initialCollectionInfo}
      initialNfts={initialNfts}
    />
  )
}
